import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { radius, SurfacePalette, type } from '../../../tokens'
import { useSectionNumber } from '../../PacketSectionsContext'
import { PresentPage } from '../PresentPage'

interface Props {
  palette: SurfacePalette
  packetID: number
  surveyLink: string
}

// Present-mode survey slide: the Salesforce survey QR, and nothing else.
//
// There is deliberately no submitted count. Responses land in Salesforce now, so
// our own count is structurally zero forever — a big fat 0 on a projector next to
// "Scan to share your feedback", in front of the client. Read-only, so no
// PresentNavLock.
//
// Takes the link as a plain `string`, not the nullable status: a packet with no
// link has no survey slide at all, so the host filters it out of the deck and
// this component never has to render an empty state.
export const PresentClientSurveySection = ({ palette, packetID, surveyLink }: Props) => (
  <PresentPage
    palette={palette}
    number={useSectionNumber('client-survey')}
    title="Client Survey"
    accentWord="Survey"
    commentAnchor={{ type: 'section', id: `${packetID}:client-survey`, label: 'Client Survey' }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 56,
        paddingTop: 12,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: 20,
          borderRadius: radius.lg,
          background: '#fff',
        }}
      >
        <QRCodeSVG value={surveyLink} size={280} />
      </div>
      <div style={{ maxWidth: 440 }}>
        <div
          style={{
            fontFamily: type.family.serif,
            fontSize: type.size.h2,
            color: palette.text,
            marginBottom: 8,
          }}
        >
          Scan to share your feedback
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: type.family.sans,
            fontSize: type.size.body,
            color: palette.textMuted,
            lineHeight: 1.5,
          }}
        >
          Answer privately on your own phone — your responses go straight to our Client Success team
          and never appear on this screen.
        </p>
      </div>
    </div>
  </PresentPage>
)
